/**
 * Phase 4 Task 12 — Supervisor Dashboard page.
 *
 * Mounts the typed supervisor:* socket wiring and renders the live
 * agent grid + active-call monitor. Forced agent actions
 * (`force_pause`, `force_logout`) emit through the gateway, which
 * publishes a `SupervisorActionEvent` to RabbitMQ — the worker
 * consumer (Task 8) carries them out. Listen / whisper / barge are
 * REST calls handled inside `CallMonitor`.
 *
 * Scoping note: the gateway data feed is a stub for Task 12. The UI
 * subscribes and renders empty snapshots until the Phase 4 follow-up
 * wires the real Redis aggregation + AMQP fanout.
 *
 * UI-6b Task 18 — Default-dashboard switch.
 *
 * If the tenant has configured a default dashboard for the 'supervisor'
 * role (via the DashboardComposer page), this landing route renders that
 * custom dashboard instead of the legacy Live Agents + Active Calls
 * panels. When no default is configured (`dashboard === null`), we fall
 * back to the legacy view.
 *
 * The socket subscription stays mounted unconditionally so the supervisor
 * store keeps receiving updates regardless of which view is active — that
 * way widgets in a custom dashboard that happen to consume the same
 * store (e.g. an "active agents" widget) work without extra wiring, and
 * toggling the default flag in another tab doesn't drop the live feed.
 *
 * UI-9d Task 7 — Responsive treatment (Phase 1).
 *
 * The supervisor view stays single-column (vertical stack of Panels) at
 * every breakpoint — the existing layout already wraps naturally on
 * phone. Phase 1 only tightens the outer padding/gap (`p-2 gap-2` on
 * phone, `lg:p-4 lg:gap-4` desktop) so the Panels aren't cramped against
 * the viewport edges on a small screen. `AgentGrid` itself already
 * declares `grid-cols-2 md:grid-cols-3 lg:grid-cols-4`, so the tiles wrap
 * cleanly at every breakpoint.
 *
 * Phase 2 (deferred — see plan §3.5) will introduce the full
 * mobile-list-then-detail navigation pattern (master list of agents/calls
 * on phone, drill-down detail page) and a `useBreakpoint`-driven
 * collapsed monitor view.
 */
import { useEffect, useRef, type JSX } from 'react'
import { Panel } from 'primereact/panel'
import { Message } from 'primereact/message'
import { ProgressSpinner } from 'primereact/progressspinner'
import { getSocket } from '../../socket/client.js'
import { wireSupervisorEvents } from '../../socket/supervisor-events.js'
import { useSupervisorStore } from '../../store/supervisorStore.js'
import { AgentGrid } from '../../modules/supervisor/AgentGrid.js'
import { CallMonitor } from '../../modules/supervisor/CallMonitor.js'
import { useDefaultDashboard } from '../../hooks/useDefaultDashboard.js'
import DashboardRenderer from '../../components/dashboards/DashboardRenderer.js'
import { useBreakpoint } from '../../hooks/useBreakpoint.js'
import SupervisorMobileView from './SupervisorMobileView.js'

export default function SupervisorDashboard(): JSX.Element {
  const agentList = useSupervisorStore((s) => s.agentList)
  const liveCalls = useSupervisorStore((s) => s.liveCalls)
  const alerts = useSupervisorStore((s) => s.alerts)
  const clearAlert = useSupervisorStore((s) => s.clearAlert)

  const { dashboard, loading } = useDefaultDashboard('supervisor')
  const breakpoint = useBreakpoint()

  const wiredRef = useRef(false)

  useEffect(() => {
    if (wiredRef.current) return
    wiredRef.current = true

    const socket = getSocket()
    const cleanup = wireSupervisorEvents(socket)
    socket.emit('supervisor:subscribe')

    return () => {
      socket.emit('supervisor:unsubscribe')
      cleanup()
      wiredRef.current = false
    }
  }, [])

  function handleForcePause(agentId: string): void {
    getSocket().emit('supervisor:force_pause', { agentId })
  }
  function handleForceLogout(agentId: string): void {
    getSocket().emit('supervisor:force_logout', { agentId })
  }

  if (loading) {
    return (
      <div
        className="p-4 flex justify-center"
        data-testid="supervisor-dashboard-loading"
      >
        <ProgressSpinner />
      </div>
    )
  }

  // Batch E commit E2 — Phase 2 mobile master/detail flow. Phone users
  // get a list-then-drill-down experience via `<SupervisorMobileView />`
  // (Agents | Calls tabs → `/supervisor/agents/:agentId` and
  // `/supervisor/calls/:callId` detail routes). Tablet + desktop keep
  // the AgentGrid + CallMonitor panel layout. The socket subscription
  // wired above stays mounted across breakpoints so live updates flow
  // into the store identically.
  if (breakpoint === 'phone') {
    return (
      <div className="flex h-full min-h-0 flex-col gap-2">
        {alerts.length > 0 && (
          <div className="flex flex-col gap-2 p-2" data-testid="alerts">
            {alerts.map((a) => (
              <Message
                key={a.id}
                severity={a.severity === 'error' ? 'error' : a.severity}
                text={a.message}
                onClick={() => clearAlert(a.id)}
              />
            ))}
          </div>
        )}
        <SupervisorMobileView />
      </div>
    )
  }

  if (dashboard) {
    return (
      <div className="p-2 lg:p-4 flex flex-col gap-2 lg:gap-4">
        {alerts.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="alerts">
            {alerts.map((a) => (
              <Message
                key={a.id}
                severity={a.severity === 'error' ? 'error' : a.severity}
                text={a.message}
                onClick={() => clearAlert(a.id)}
              />
            ))}
          </div>
        )}
        <DashboardRenderer layout={dashboard.layout} />
      </div>
    )
  }

  return (
    <div className="p-2 lg:p-4 flex flex-col gap-2 lg:gap-4">
      {alerts.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="alerts">
          {alerts.map((a) => (
            <Message
              key={a.id}
              severity={a.severity === 'error' ? 'error' : a.severity}
              text={a.message}
              onClick={() => clearAlert(a.id)}
            />
          ))}
        </div>
      )}
      <Panel header="Live Agents">
        <AgentGrid
          agents={agentList}
          onForcePause={handleForcePause}
          onForceLogout={handleForceLogout}
        />
      </Panel>
      <Panel header="Active Calls">
        <CallMonitor calls={liveCalls} />
      </Panel>
    </div>
  )
}
